import { useEffect, useState } from 'react';
import { debridApi, metaApi, type DebridState } from '../api';
import { savePrefs, usePrefs } from '../prefs';
import { linkProps } from '../router';
import { deviceClass, setDeviceClass, setTitleLayout, suggestDeviceClass, useTitleLayout, type DeviceClass } from '../theme';
import { ThemePicker } from './ThemePicker';
import { QrCode } from './QrCode';
import { useApp } from '../context';

/**
 * Snel instellen (zoals DexHub's Quick setup): vier stappen op één scherm.
 * 1. accounts koppelen, 2. hoe titels starten, 3. je look, 4. wat voor apparaat dit is.
 * Alles wordt meteen bewaard; "Alle instellingen" staat in het menu links.
 */
export function QuickSetup() {
  const prefs = usePrefs();
  const [trakt, setTrakt] = useState<{ configured: boolean; linked: boolean; username: string | null } | null>(null);
  const [debrid, setDebrid] = useState<(DebridState & { allowed: boolean }) | null>(null);
  const [layout] = useTitleLayout();
  const [device, setDevice] = useState<DeviceClass | null>(deviceClass());
  const suggested = suggestDeviceClass();

  useEffect(() => {
    metaApi.trakt().then(setTrakt).catch(() => setTrakt(null));
    debridApi.get().then(setDebrid).catch(() => setDebrid(null));
  }, []);

  const pickDevice = (d: DeviceClass) => {
    setDeviceClass(d);
    setDevice(d);
    // Licht apparaat: geen trailers in de spotlight (scheelt veel)
    void savePrefs({ spotlightTrailers: d !== 'light' });
  };

  const linkedDebrid = debrid?.providers.filter((p) => p.linked) ?? [];
  const { me, switchProfile } = useApp();
  const myUuid = me.profile?.uuid ?? '';
  // Geopend via de QR-code van een tv: voor welk profiel is het bedoeld?
  const forParams = new URLSearchParams(window.location.search);
  const forUuid = forParams.get('profile');
  const forName = forParams.get('name');
  const remote = !!forUuid;
  const phoneUrl = `${window.location.origin}/settings/quick?profile=${encodeURIComponent(myUuid)}&name=${encodeURIComponent(me.profile?.title ?? '')}`;

  return (
    <div className="quick-setup">
      {remote && forUuid !== myUuid && (
        <div className="qs-warn" role="status">
          <span>Je stelt nu in voor <strong>{me.profile?.title ?? 'dit profiel'}</strong>, maar de tv gebruikt <strong>{forName || 'een ander profiel'}</strong>. Wissel van profiel om de tv in te stellen.</span>
          <button className="btn btn-sm" onClick={switchProfile}>Profiel wisselen</button>
        </div>
      )}
      <p className="subtle hint">In vier stappen klaar. Alles wordt meteen bewaard; wil je meer, kies dan links een onderdeel of "Alle instellingen".</p>

      {!remote && myUuid && (
        <section className="qs-phone" aria-label="Instellen met je telefoon">
          <QrCode text={phoneUrl} size={168} label="QR-code om tumedia op je telefoon in te stellen" />
          <div>
            <h3>Liever op je telefoon?</h3>
            <p className="subtle">Scan de code met je telefoon: dan stel je dit profiel ({me.profile?.title}) daar in, zonder te typen met de afstandsbediening. Alles wat je op je telefoon kiest, geldt meteen ook hier.</p>
          </div>
        </section>
      )}

      <section className="qs-step" aria-labelledby="qs-1">
        <span className="qs-num" aria-hidden="true">1</span>
        <div className="qs-body">
          <h3 id="qs-1">Je accounts</h3>
          <div className="qs-links">
            {trakt?.configured && (
              <a className={`qs-link${trakt.linked ? ' is-done' : ''}`} {...linkProps('/settings/connections')}>
                <strong>Trakt</strong>
                <span>{trakt.linked ? `Gekoppeld${trakt.username ? ` als ${trakt.username}` : ''}` : 'Kijkgeschiedenis en aanraders, op al je apps'}</span>
              </a>
            )}
            {debrid?.allowed && (
              <a className={`qs-link${linkedDebrid.length ? ' is-done' : ''}`} {...linkProps('/settings/debrid')}>
                <strong>Debrid</strong>
                <span>{linkedDebrid.length ? `${linkedDebrid.map((p) => p.name).join(', ')} gekoppeld` : 'TorBox, Real-Debrid, AllDebrid of Premiumize: meteen afspelen'}</span>
              </a>
            )}
            <a className="qs-link" {...linkProps('/settings/household')}>
              <strong>Profielen en kinderslot</strong>
              <span>Pincode, bedtijd en leeftijdsgrens</span>
            </a>
          </div>
        </div>
      </section>

      <section className="qs-step" aria-labelledby="qs-2">
        <span className="qs-num" aria-hidden="true">2</span>
        <div className="qs-body">
          <h3 id="qs-2">Hoe titels starten</h3>
          <div className="qs-cards" role="radiogroup" aria-label="Hoe titels starten">
            <button type="button" role="radio" aria-checked={!prefs.autoPick} className={`qs-card${!prefs.autoPick ? ' is-on' : ''}`} onClick={() => void savePrefs({ autoPick: false })}>
              <strong>Zelf een bron kiezen</strong>
              <span>Je ziet alle bronnen per kwaliteit en kiest er een</span>
            </button>
            <button type="button" role="radio" aria-checked={prefs.autoPick} className={`qs-card${prefs.autoPick ? ' is-on' : ''}`} onClick={() => void savePrefs({ autoPick: true })}>
              <strong>Meteen de beste bron</strong>
              <span>Eén knop Afspelen; tumedia test en kiest de beste</span>
            </button>
          </div>
          <div className="qs-chips" role="radiogroup" aria-label="Kwaliteit">
            {([['best', 'Beste kwaliteit', '4K en HDR waar het kan'], ['balanced', 'In balans', '1080p, snel en scherp'], ['saver', 'Zuinig', 'Kleinere bestanden, trage verbinding']] as const).map(([v, name, hint]) => (
              <button key={v} type="button" role="radio" aria-checked={prefs.qualityProfile === v} className={`chip${prefs.qualityProfile === v ? ' is-on' : ''}`} aria-pressed={prefs.qualityProfile === v} title={hint} onClick={() => void savePrefs({ qualityProfile: v })}>{name}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="qs-step" aria-labelledby="qs-3">
        <span className="qs-num" aria-hidden="true">3</span>
        <div className="qs-body">
          <h3 id="qs-3">Je look</h3>
          {!remote && <div className="qs-cards" role="radiogroup" aria-label="Titelpagina">
            {([['classic', 'Klassieke titelpagina', 'Achtergrond bovenaan, de rest eronder'], ['cinematic', 'Cinematic titelpagina', 'Achtergrond over de hele pagina, knoppen van glas']] as const).map(([v, name, hint]) => (
              <button key={v} type="button" role="radio" aria-checked={layout === v} className={`qs-card qs-card-pic${layout === v ? ' is-on' : ''}`} onClick={() => setTitleLayout(v)}>
                <span className={`pic-thumb pic-${v}`} aria-hidden="true">
                  <i className="p-art" /><i className="p-scrim" /><i className="p-l1" /><i className="p-l2" /><i className="p-l3" /><i className="p-btn" /><i className="p-row" />
                </span>
                <strong>{name}</strong>
                <span>{hint}</span>
              </button>
            ))}
          </div>}
          <ThemePicker />
        </div>
      </section>

      {!remote && (
      <section className="qs-step" aria-labelledby="qs-4">
        <span className="qs-num" aria-hidden="true">4</span>
        <div className="qs-body">
          <h3 id="qs-4">Dit apparaat</h3>
          <div className="qs-cards" role="radiogroup" aria-label="Dit apparaat">
            {([['light', 'Licht', 'Oudere tv of stick: geen trailers op de achtergrond, minder beweging'], ['balanced', 'Gemiddeld', 'De meeste laptops en tablets'], ['strong', 'Sterk', 'Snelle computer of nieuwe tv: alles aan']] as const).map(([v, name, hint]) => (
              <button key={v} type="button" role="radio" aria-checked={(device ?? suggested) === v} className={`qs-card${(device ?? suggested) === v ? ' is-on' : ''}`} onClick={() => pickDevice(v)}>
                <strong>{name}{v === suggested ? ' · voorstel' : ''}</strong>
                <span>{hint}</span>
              </button>
            ))}
          </div>
          <p className="subtle hint">Geldt alleen voor deze browser; elk apparaat onthoudt zijn eigen keuze.</p>
        </div>
      </section>
      )}

      <a className="btn" {...linkProps('/settings/av')}>Alle instellingen</a>
    </div>
  );
}
